// @file Surface wish arrangement store
import window from '@@/bits/global'
import { BLANK_GROUP, Group, ORDERED_COLOR_GROUPS } from '@@/bits/surface_posts_grouping_helper'
import { ManualSortBy, ManualSortByArray } from '@@/bits/surface_settings_helper'
import { GroupByTypes, SortByTypes } from '@padlet/arvo'
import { WishArrangement } from '@padlet/arvo/lib/types'
import { defineStore, storeToRefs } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfaceSectionsStore } from 'pinia/surface_sections'
import { useSurfaceSettingsStore } from 'pinia/surface_settings'
import { computed } from 'vue'

export const useSurfaceWishArrangementStore = defineStore('surfaceWishArrangement', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia

  const { canUseSections, isSectioned } = storeToRefs(useSurfaceStore())
  const { sortedSections } = storeToRefs(useSurfaceSectionsStore())
  const {
    isPreviewing,
    wishGroupBy: previewWishGroupBy,
    wishSortBy,
    previewAttributes,
  } = storeToRefs(useSurfaceSettingsStore())

  const previewWishArrangement = computed<WishArrangement | undefined>(() => previewAttributes.value.wish_arrangement)

  const wishGroupBy = computed<GroupByTypes>(() => {
    if (!canUseSections.value) return GroupByTypes.None
    return isPreviewing.value ? previewWishGroupBy.value : surfaceVuexStore?.getters.wishGroupBy
  })
  const isSectionedAndNonmanualSorted = computed<boolean>(() => !areWishesManuallySorted.value && isSectioned.value)
  const isNotSectionedAndNonmanualSorted = computed<boolean>(() => !areWishesManuallySorted.value && !isSectioned.value)
  const originalWishSortBy = computed(() => surfaceVuexStore?.getters.wishSortBy)
  const previewWishSortBy = computed(() => wishSortBy.value)
  const hasWishSortByChanged = computed(() => previewWishSortBy.value !== originalWishSortBy.value)
  const defaultWishSortBy = computed<SortByTypes>(() => surfaceVuexStore?.getters.defaultWishSortBy)
  const areWishesManuallySorted = computed<boolean>(() =>
    ManualSortByArray.includes(originalWishSortBy.value as ManualSortBy),
  )

  const sortedGroups = computed<Group[]>(() => {
    switch (wishGroupBy.value) {
      case GroupByTypes.Section:
        return sortedSections.value
      case GroupByTypes.Color:
        return ORDERED_COLOR_GROUPS
      case GroupByTypes.None:
      default:
        return [BLANK_GROUP]
    }
  })

  return {
    // getters
    isNotSectionedAndNonmanualSorted,
    isSectionedAndNonmanualSorted,
    wishGroupBy,
    defaultWishSortBy,
    sortedGroups,
    originalWishSortBy,
    previewWishSortBy,
    previewWishArrangement,
    hasWishSortByChanged,
    areWishesManuallySorted,
  }
})
